import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { AddMediaElementCommand } from './AddMediaElementCommand';
import { TimelineMode } from './TimelineMode';

import type { Element } from '../models/Element';
import type { Track } from '../models/Track';
import type { VideoEditor } from '../VideoEditor';

export interface AddMusicElementOptions {
  /**
   * If true, adjusts the music duration to fit from its start time to the end of the timeline
   */
  fitToEnd?: boolean;

  /**
   * Default volume for the music element (0-100)
   */
  volume?: number;

  /**
   * Whether to automatically add fade in/out effects
   */
  autoFade?: boolean;
}

/**
 * Specialized command for adding music/audio elements to the timeline.
 * Extends AddMediaElementCommand with music-specific features:
 * - Fit to end option: automatically adjusts duration to fill to timeline end
 * - Volume control: sets initial volume level
 * - Auto fade: applies fade in/out effects
 * - Track placement: prioritizes bottom-most audio tracks for music placement, filling gaps before creating new tracks
 */
export class AddMusicElementCommand extends AddMediaElementCommand {
  private options: AddMusicElementOptions;

  getName(): string {
    return 'AddMusicElementCommand';
  }
  private originalDuration?: number;
  private calculatedDuration?: number;

  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode: TimelineMode = TimelineMode.DEFAULT,
    options: AddMusicElementOptions = {},
  ) {
    super(editor, element, targetTrackId, mode);
    this.options = {
      fitToEnd: false,
      volume: 10, // Default volume from original implementation
      autoFade: false,
      ...options,
    };

    // Store original duration before any modifications
    this.originalDuration = element.durationSeconds;
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
      options: this.options,
    });

    // Apply music-specific modifications before executing parent logic
    if (!this.applyMusicSpecificModifications()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to apply music-specific modifications',
      });
      return false;
    }

    // Call parent execute which handles the core adding logic
    return super.execute();
  }

  undo(): boolean {
    // Restore original duration if it was modified
    if (
      this.originalDuration !== undefined &&
      this.calculatedDuration !== undefined
    ) {
      this.element.update({ duration: this.originalDuration });
    }

    // Call parent undo logic
    return super.undo();
  }

  getDescription(): string {
    const baseDescription = super.getDescription();
    const musicFeatures: string[] = [];

    if (this.options.fitToEnd) {
      musicFeatures.push('fit-to-end');
    }
    if (this.options.volume !== undefined && this.options.volume !== 10) {
      musicFeatures.push(`volume: ${this.options.volume}`);
    }
    if (this.options.autoFade) {
      musicFeatures.push('auto-fade');
    }

    if (musicFeatures.length > 0) {
      return `${baseDescription} [${musicFeatures.join(', ')}]`;
    }

    return baseDescription;
  }

  /**
   * Gets the calculated duration if fit-to-end was applied
   */
  getCalculatedDuration(): number | undefined {
    return this.calculatedDuration;
  }

  /**
   * Gets the original duration before any modifications
   */
  getOriginalDuration(): number | undefined {
    return this.originalDuration;
  }

  /**
   * Override the findOrCreateTargetTrack method to implement music-specific logic
   */
  protected findOrCreateTargetTrack(): Track | undefined {
    // If specific track ID provided, use it (unless it has collisions in DEFAULT mode)
    if (this.targetTrackId) {
      const track = this.editor.getTrackById(this.targetTrackId);
      if (track) {
        // In DEFAULT mode, if target track has collision, create new track
        if (this.mode === TimelineMode.DEFAULT && this.hasCollision(track)) {
          VideoEditorLogger.logTimelineOperation(
            'add_music_element_collision',
            {
              message: `Target track ${this.targetTrackId} has collision, creating new track`,
            },
          );
          return this.createNewTrack();
        }
        return track;
      }
      VideoEditorLogger.logTimelineOperation(
        'add_music_element_track_not_found',
        {
          warning: `Target track ${this.targetTrackId} not found, will auto-select`,
        },
      );
    }

    // Auto-select compatible track based on music-specific logic
    const compatibleTrack = this.findCompatibleMusicTrack();
    if (compatibleTrack) {
      return compatibleTrack;
    }

    // Create new track if no compatible track found
    return this.createNewTrack();
  }

  /**
   * Music-specific track finding logic to prefer audio tracks and handle gap filling
   * Prioritizes bottom-most (lowest ID) audio tracks for music placement
   */
  private findCompatibleMusicTrack(): Track | undefined {
    // Get all tracks sorted by ID (ascending) to start from bottom
    const audioTracks = [...this.editor.timeline.tracks]
      .filter(track => track.type === 'audio')
      .sort((a, b) => {
        const aId = Number(a.id);
        const bId = Number(b.id);
        return aId - bId; // Ascending order (bottom to top)
      });

    if (audioTracks.length === 0) {
      return undefined;
    }

    // For DEFAULT mode, check each track starting from bottom
    if (this.mode === TimelineMode.DEFAULT) {
      for (const track of audioTracks) {
        if (!this.hasCollision(track)) {
          return track;
        }
      }

      // Create new track if no compatible audio track found
      return undefined;
    }

    // For RIPPLE and OVERLAP modes, find the bottom-most audio track
    return audioTracks.length > 0 ? audioTracks[0] : undefined;
  }

  /**
   * Applies music-specific modifications to the element
   */
  private applyMusicSpecificModifications(): boolean {
    try {
      const modifications: Record<string, unknown> = {};

      // Apply volume setting (as number for AudioElement.update to convert)
      if (this.options.volume !== undefined) {
        modifications.volume = this.options.volume;
      }

      // Apply fit-to-end logic
      if (this.options.fitToEnd) {
        const fittedDuration = this.calculateFitToEndDuration();
        if (fittedDuration === null) {
          VideoEditorLogger.logCommand(this, 'error', {
            error: 'Cannot fit music to end - insufficient space available',
          });
          return false;
        }
        modifications.duration = fittedDuration;
        this.calculatedDuration = fittedDuration;
      }

      // Apply auto-fade effects (use Creatomate property names)
      if (this.options.autoFade) {
        modifications.audio_fade_in = 1.0; // 1 second fade in
        modifications.audio_fade_out = 1.0; // 1 second fade out
      }

      // Apply all modifications at once using the proper update method
      if (Object.keys(modifications).length > 0) {
        this.element.update(modifications);

        VideoEditorLogger.logTimelineOperation(
          'add_music_element_modifications_applied',
          {
            action: 'applied_music_modifications',
            modifications,
          },
        );
      }

      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to apply music modifications',
        details: error,
      });
      return false;
    }
  }

  /**
   * Calculates the duration needed to fit the music from its start time to the end of the timeline
   * Only applies fitToEnd if the original music would exceed the timeline length
   */
  private calculateFitToEndDuration(): number | null {
    const elementStartTime = this.element.time;
    const originalDuration =
      this.originalDuration || this.element.durationSeconds;
    const originalEndTime = elementStartTime + originalDuration;
    const timelineEndTime = this.calculateTimelineEndTime();

    // Only apply fitToEnd if the original music would exceed the timeline
    if (originalEndTime <= timelineEndTime) {
      return originalDuration;
    }

    // Music would exceed timeline, calculate fitted duration
    if (timelineEndTime <= elementStartTime) {
      return null; // No space available
    }

    const availableDuration = timelineEndTime - elementStartTime;
    const minimumDuration = 1.0; // Minimum 1 second

    if (availableDuration < minimumDuration) {
      return null; // Not enough space for meaningful music
    }

    VideoEditorLogger.logTimelineOperation(
      'add_music_element_fit_to_end_calculated',
      {
        action: 'calculated_fit_to_end_duration',
        elementStartTime,
        timelineEndTime,
        availableDuration,
      },
    );

    return availableDuration;
  }

  /**
   * Calculates the end time of the timeline based on all existing elements
   */
  private calculateTimelineEndTime(): number {
    let maxEndTime = 0;

    for (const track of this.editor.timeline.tracks) {
      for (const element of track.elements) {
        const elementEndTime = element.time + element.durationSeconds;
        maxEndTime = Math.max(maxEndTime, elementEndTime);
      }
    }

    // If no elements exist, use a default duration
    return maxEndTime > 0 ? maxEndTime : 30; // Default to 30 seconds
  }

  /**
   * Checks if the element can fit in the gaps of a track
   */
  private canFitInTrackGaps(
    track: Track,
    startTime: number,
    endTime: number,
  ): boolean {
    // Sort elements by start time
    const sortedElements = [...track.elements].sort((a, b) => a.time - b.time);

    // Check if we can fit before the first element
    if (sortedElements.length === 0 || endTime <= sortedElements[0].time) {
      return true;
    }

    // Check gaps between elements
    for (let i = 0; i < sortedElements.length - 1; i++) {
      const currentElementEnd =
        sortedElements[i].time + sortedElements[i].durationSeconds;
      const nextElementStart = sortedElements[i + 1].time;

      if (startTime >= currentElementEnd && endTime <= nextElementStart) {
        return true;
      }
    }

    // Check if we can fit after the last element
    const lastElement = sortedElements[sortedElements.length - 1];
    const lastElementEnd = lastElement.time + lastElement.durationSeconds;
    if (startTime >= lastElementEnd) {
      return true;
    }

    return false;
  }

  /**
   * Override parent's collision detection to use music-specific logic
   */
  protected hasCollision(track: Track): boolean {
    // In music mode, we want to check for gaps as well as collisions
    if (this.mode === TimelineMode.DEFAULT) {
      // Use gap-aware collision detection
      return !this.canFitInTrackGaps(
        track,
        this.element.time,
        this.element.time + this.element.durationSeconds,
      );
    }

    // For other modes, use parent collision detection
    return super.hasCollision(track);
  }

  /**
   * Creates a new audio track specifically for music at the bottom of the timeline
   */
  protected createNewTrack(): Track | undefined {
    try {
      this.createdTrack = this.createBottomAudioTrack();
      this.trackCreated = true;

      VideoEditorLogger.logTimelineOperation(
        'add_music_element_track_created',
        {
          action: 'audio_track_created',
          trackId: this.createdTrack.id,
          trackType: 'audio',
        },
      );

      return this.createdTrack;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to create new audio track at bottom',
        details: error,
      });
      return undefined;
    }
  }

  /**
   * Creates a new audio track at the bottom of the timeline by shifting existing tracks up
   */
  private createBottomAudioTrack(): Track {
    const timeline = this.editor.timeline;
    const trackManager = this.editor.getTrackManager();

    // Get all existing tracks sorted by ID
    const existingTracks = [...timeline.tracks].sort((a, b) => {
      const aId = Number(a.id);
      const bId = Number(b.id);
      return aId - bId; // Ascending order
    });

    // If no tracks exist, create track 1
    if (existingTracks.length === 0) {
      return trackManager.createTrack('audio');
    }

    // Find the lowest track ID
    const lowestTrackId = Number(existingTracks[0].id);

    // If the lowest track is already 1, we need to shift all tracks up
    if (lowestTrackId === 1) {
      this.shiftAllTracksUp();
    }

    // Create new track with ID 1 (bottom position)
    const newTrack = timeline.createTrack('audio', '1');

    VideoEditorLogger.logTimelineOperation(
      'add_music_element_bottom_track_created',
      {
        action: 'created_bottom_audio_track',
        newTrackId: '1',
        shiftedTracks: lowestTrackId === 1,
      },
    );

    return newTrack;
  }

  /**
   * Shifts all existing tracks up by 1 to make room at the bottom (track 1)
   */
  private shiftAllTracksUp(): void {
    const timeline = this.editor.timeline;
    const tracks = [...timeline.tracks]
      .filter(track => {
        const trackId = Number(track.id);
        // Exclude karaoke tracks and any invalid track IDs
        return (
          !Number.isNaN(trackId) &&
          trackId >= 1 &&
          trackId < KARAOKE_TRACK_NUMBER
        );
      })
      .sort((a, b) => {
        const aId = Number(a.id);
        const bId = Number(b.id);
        return bId - aId; // Descending order (start from highest)
      });

    // Collect track updates to apply all at once
    const trackUpdates: Array<{ track: Track; oldId: string; newId: string }> =
      [];

    // Shift tracks from highest to lowest to avoid ID conflicts
    for (const track of tracks) {
      const currentId = Number(track.id);
      const newId = String(currentId + 1);

      // Validate new ID is within allowed range
      if (currentId + 1 > 31) {
        throw new Error(
          'Cannot shift tracks: would exceed maximum track ID of 31',
        );
      }

      trackUpdates.push({
        track,
        oldId: String(currentId),
        newId,
      });
    }

    // Apply all track updates
    for (const { track, oldId, newId } of trackUpdates) {
      // Update track ID and all its elements
      track.setId(newId);
      for (const element of track.elements) {
        element.update({ track: Number(newId) });
      }

      // Remove track from timeline with old ID and add back with new ID
      timeline.removeTrack(oldId);
      timeline.addExistingTrack(track);

      VideoEditorLogger.logTimelineOperation(
        'add_music_element_track_shifted',
        {
          action: 'shifted_track_up',
          oldTrackId: oldId,
          newTrackId: newId,
          elementCount: track.elements.length,
        },
      );
    }

    // Sort tracks by numeric ID to maintain proper order
    timeline.sortTracksByNumericId();
  }
}
